import React, { useState } from 'react';
import { Button, message, Upload } from 'antd';

import { API_ROOT, authenticatedUploadProps } from '@partsbadger/utils';
import QuoteStore from '../../stores/QuoteStore';
import { UploadProps } from 'antd/es/upload';
import { QuoteLineItemProps } from '@partsbadger/types';
import { allowed3d } from '@partsbadger/library/src/UploadFile/FileExtensions';
import { UploadOutlined } from '@ant-design/icons';
import { getUpload3dEffect } from '../../utils/upload3dEvents';
import {
    getUploadButtonLabel,
    getUploadLabelClass,
    isUploadInProgress,
    UploadStage,
    uploadStageForStatus,
} from '../../utils/uploadProgress';
import type { RcFile } from 'antd/es/upload';

type ButtonType = 'default' | 'compact';

interface Props extends UploadProps {
    item: QuoteLineItemProps;
    buttonType?: ButtonType;
    buttonText?: string;
    /** Tells the part when its 3D file is travelling or being saved (to hide "Missing 3D file"). */
    onUploadStateChange?: (uploading: boolean) => void;
    'data-testid'?: string;
}

export const Upload3dFile = ({
    item,
    buttonType = 'default',
    buttonText,
    onUploadStateChange,
    'data-testid': testId,
}: Props) => {
    const defaultText = 'Upload 3D File';
    const text = buttonText ?? defaultText;

    const allowedExtensions = allowed3d.map(ext => ext.toLowerCase());

    const [uploadStage, setUploadStage] = useState<UploadStage>('idle');
    const [pendingFilename, setPendingFilename] = useState('');
    const [uploadPercent, setUploadPercent] = useState(0);
    const uploadInProgress = isUploadInProgress(uploadStage);

    const finishUpload = () => {
        setUploadStage('idle');
        setUploadPercent(0);
        onUploadStateChange?.(false);
    };

    // A signed in user needs the token (like the 2D upload); a guest is identified by the signed uid.
    const uploadUrl = `${API_ROOT}/customer/quote/${QuoteStore.quote_uid}/3d-file/`;
    const uploadAuthProps = QuoteStore.token ? authenticatedUploadProps(uploadUrl) : { action: uploadUrl };

    const props: UploadProps = {
        name: 'file',
        multiple: false,
        accept: allowedExtensions.map(r => `.${r}`).toString(),
        ...uploadAuthProps,
        data: { part_name: item?.name },
        // The part header shows the attached file; the upload list would repeat it, or keep a failed one.
        showUploadList: false,
        beforeUpload: (file: RcFile) => {
            const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
            if (!allowedExtensions.includes(extension)) {
                message.error({
                    content: `"${file.name}" is not a valid 3D file. Please upload a 3D file format.`,
                    duration: 8,
                    key: 'invalid-3d-file',
                });
                return Upload.LIST_IGNORE;
            }
            return true;
        },
        onChange: async (info: any) => {
            const stage = uploadStageForStatus(info.file?.status);

            if (info.file?.status === 'uploading') {
                onUploadStateChange?.(true);
                setPendingFilename(info.file.name);
                setUploadPercent(Math.round(info.file.percent || 0));
            }
            if (stage) setUploadStage(stage);

            const effect = getUpload3dEffect(info);

            if (effect?.type === 'attach') {
                try {
                    const saved = await QuoteStore.updateLineItemFiles({ id: item.id, product: effect.payload });
                    if (!saved) {
                        throw new Error('The 3D file was not attached');
                    }
                } catch {
                    message.error({
                        content: 'The 3D file could not be saved to this part. Please try again.',
                        duration: 8,
                        key: 'upload-3d-error',
                    });
                } finally {
                    finishUpload();
                }
            } else if (effect?.type === 'detach') {
                finishUpload();
                void QuoteStore.updateLineItemFiles({ id: item.id, product: { file_3d: null } });
            } else if (effect?.type === 'error') {
                finishUpload();
                message.error({ content: effect.message, duration: 8, key: 'upload-3d-error' });
            }
        },
    };

    if (!item?.uid) {
        return null;
    }

    const label = getUploadButtonLabel(uploadStage, pendingFilename, uploadPercent, text);

    const renderButton = (type: ButtonType) => {
        switch (type) {
            case 'compact':
                // In the Part Header, upload is a secondary inline action —
                // visually consistent with an attached filename/preview link.
                return (
                    <Button
                        size="small"
                        className="p-0"
                        data-testid={testId}
                        type="link"
                        disabled={uploadInProgress}
                        loading={uploadInProgress}
                        icon={uploadInProgress ? undefined : <UploadOutlined />}
                        style={{ display: 'inline-flex', alignItems: 'center' }}
                    >
                        <span aria-live="polite" className={getUploadLabelClass(uploadStage)}>
                            {label}
                        </span>
                    </Button>
                );

            case 'default':
            default:
                // Same neutral "upload" style/icon as the compact variant —
                // only the size changes between contexts, never the color or
                // icon, so this action always reads as "upload" everywhere.
                return (
                    <Button
                        className="d-flex align-items-center"
                        data-testid={testId}
                        size="small"
                        style={{
                            fontSize: '13px',
                            lineHeight: '2rem',
                            height: '2rem',
                        }}
                        type={'default'}
                        disabled={uploadInProgress}
                        loading={uploadInProgress}
                        icon={uploadInProgress ? undefined : <UploadOutlined />}
                    >
                        <span aria-live="polite" className={getUploadLabelClass(uploadStage)}>
                            {label}
                        </span>
                    </Button>
                );
        }
    };

    return <Upload {...props}>{renderButton(buttonType)}</Upload>;
};
